/**
 * Servicio de API para Autenticación
 * Maneja registro, login y operaciones de autenticación
 */

import apiClient from './apiClient';
import type {
    LoginDto,
    UserResponse,
    LoginResponse,
    CreateUserDto
} from '../../types/model/user.model';

/**
 * Respuesta estándar de la API
 */
interface ApiResponse<T> {
    success: boolean;
    data: T;
    message?: string;
}

/**
 * DTO para backend (username en lugar de nombre/apellido)
 */

/**
 * Servicio de autenticación
 */
export const authService = {
    /**
     * Registra un nuevo usuario
     * @param data - Datos del formulario (nombre, apellido, email, password)
     * @returns Usuario registrado
     */
    register: async (data: CreateUserDto): Promise<UserResponse> => {
        const backendData: CreateUserDto = {
            username: data.username,
            email: data.email,
            password: data.password
        };

        const response = await apiClient.post<ApiResponse<UserResponse>>(
            '/users/register',
            backendData
        );

        return response.data.data;
    },

    /**
     * Inicia sesión de un usuario
     * @param data - Credenciales (email, password)
     * @returns Usuario autenticado
     */
    login: async (data: LoginDto): Promise<LoginResponse> => {
        const response = await apiClient.post<ApiResponse<LoginResponse>>(
            '/users/login',
            data
        );

        return response.data.data;
    },

    /**
     * Cierra sesión del usuario
     */
    logout: (): void => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    },

    /**
     * Obtiene el usuario autenticado del localStorage
     * @returns Usuario autenticado o null
     */
    getCurrentUser: (): UserResponse | null => {
        const userString = localStorage.getItem('user');
        if (!userString) return null;

        try {
            return JSON.parse(userString);
        } catch {
            return null;
        }
    },

    /**
     * Guarda el usuario en localStorage
     * @param user - Usuario a guardar
     */
    saveUser: (user: UserResponse): void => {
        localStorage.setItem('user', JSON.stringify(user));
    }
};

export default authService;
